/**
 * src/auth/roles.ts
 *
 * Rôles utilisateur et matrice de permissions.
 */

// ── Types ──────────────────────────────────────────────────────────────────────

export type UserRole = "super-admin" | "admin" | "manager" | "lecteur";

// ── Labels affichés dans l'UI ──────────────────────────────────────────────────

export const ROLE_LABELS: Record<UserRole, string> = {
  "super-admin": "Super Administrateur",
  "admin":       "Administrateur",
  "manager":     "Manager",
  "lecteur":     "Lecteur",
};

/** Rôles assignables via l'interface (super-admin est invisible). */
export const ASSIGNABLE_ROLES: UserRole[] = ["admin", "manager", "lecteur"];

/** Rôle par défaut pour tout nouvel utilisateur. */
export const DEFAULT_ROLE: UserRole = "lecteur";

// ── Permissions ────────────────────────────────────────────────────────────────

export interface Permissions {
  canAccessDashboard:  boolean;
  canAccessReports:    boolean;
  canAccessAnalytics:  boolean;
  canAccessData:       boolean;
  canAccessDocuments:  boolean;
  canAccessSettings:   boolean;
  canManageClients:    boolean;  // onglet Clients dans Paramètres
  canManageSites:      boolean;  // onglet Sites dans Paramètres
  canManageRoles:      boolean;  // onglet Rôles dans Paramètres (admin seulement)
  canCreateReport:     boolean;
  canDeleteReport:     boolean;
  canEditReport:       boolean;  // modification inline dans le viewer
}

export const ROLE_PERMISSIONS: Record<UserRole, Permissions> = {
  "super-admin": {
    canAccessDashboard:  true,
    canAccessReports:    true,
    canAccessAnalytics:  true,
    canAccessData:       true,
    canAccessDocuments:  true,
    canAccessSettings:   true,
    canManageClients:    true,
    canManageSites:      true,
    canManageRoles:      true,
    canCreateReport:     true,
    canDeleteReport:     true,
    canEditReport:       true,
  },
  "admin": {
    canAccessDashboard:  true,
    canAccessReports:    true,
    canAccessAnalytics:  true,
    canAccessData:       true,
    canAccessDocuments:  true,
    canAccessSettings:   true,
    canManageClients:    true,
    canManageSites:      true,
    canManageRoles:      true,
    canCreateReport:     true,
    canDeleteReport:     true,
    canEditReport:       true,
  },
  "manager": {
    canAccessDashboard:  true,
    canAccessReports:    true,
    canAccessAnalytics:  false,
    canAccessData:       false,
    canAccessDocuments:  false,
    canAccessSettings:   true,
    canManageClients:    true,
    canManageSites:      true,
    canManageRoles:      false,
    canCreateReport:     true,
    canDeleteReport:     true,
    canEditReport:       true,
  },
  "lecteur": {
    canAccessDashboard:  true,
    canAccessReports:    true,
    canAccessAnalytics:  false,
    canAccessData:       false,
    canAccessDocuments:  false,
    canAccessSettings:   false,
    canManageClients:    false,
    canManageSites:      false,
    canManageRoles:      false,
    canCreateReport:     false,
    canDeleteReport:     false,
    canEditReport:       false,
  },
};

export function getPermissions(role: UserRole): Permissions {
  return ROLE_PERMISSIONS[role];
}
